import { useState, useEffect } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabase';

interface VnGoldPriceRow {
  id: string;
  type_code: string;
  name: string;
  buy: number;
  sell: number;
  change_buy: number;
  change_sell: number;
  change_percent: number;
  updated_at: string;
}

export default function AdminGoldPrices() {
  const [prices, setPrices] = useState<VnGoldPriceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  async function fetchPrices() {
    setLoading(true);
    setErrorMsg('');
    try {
      const { data, error } = await supabase
        .from('vn_gold_prices')
        .select('*')
        .order('type_code', { ascending: true });
      if (error) throw error;
      setPrices((data || []) as VnGoldPriceRow[]);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Lỗi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdate() {
    setUpdating(true);
    setMessage('');
    setErrorMsg('');
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const url = `${supabaseUrl}/functions/v1/update-gold-vn-prices`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token ?? ''}`,
        },
      });
      const result = await res.json();
      if (res.ok && result.success) {
        setMessage(`Đã cập nhật ${result.updated} loại vàng thành công!`);
        await fetchPrices();
      } else {
        setErrorMsg(result?.error || result?.message || 'Không thể cập nhật giá');
      }
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Lỗi kết nối API giá vàng');
    } finally {
      setUpdating(false);
    }
  }

  useEffect(() => {
    fetchPrices();
  }, []);

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Giá vàng Việt Nam</h1>
            <p className="text-sm text-foreground-600 mt-1">Nguồn: giavang.now · API miễn phí · Cập nhật 5 phút/lần</p>
          </div>
          <button
            onClick={handleUpdate}
            disabled={updating}
            className="px-5 py-2.5 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap cursor-pointer transition-colors"
          >
            {updating ? 'Đang cập nhật...' : 'Cập nhật giá ngay'}
          </button>
        </div>

        {message && (
          <div className="mb-4 px-4 py-2.5 rounded-md bg-emerald-100 text-emerald-900 text-sm font-medium">
            {message}
          </div>
        )}
        {errorMsg && (
          <div className="mb-4 px-4 py-2.5 rounded-md bg-rose-100 text-rose-900 text-sm font-medium">
            {errorMsg}
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-background-200/60">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-background-100 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600">Loại vàng</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Giá mua</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Giá bán</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Thay đổi</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-600 text-right">Cập nhật lúc</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-t border-background-200/40">
                    <td className="px-4 py-3"><div className="h-4 bg-background-200 rounded w-32 animate-pulse"></div></td>
                    <td className="px-4 py-3 text-right"><div className="h-4 bg-background-200 rounded w-24 ml-auto animate-pulse"></div></td>
                    <td className="px-4 py-3 text-right"><div className="h-4 bg-background-200 rounded w-24 ml-auto animate-pulse"></div></td>
                    <td className="px-4 py-3 text-right"><div className="h-4 bg-background-200 rounded w-16 ml-auto animate-pulse"></div></td>
                    <td className="px-4 py-3 text-right"><div className="h-4 bg-background-200 rounded w-20 ml-auto animate-pulse"></div></td>
                  </tr>
                ))
              ) : prices.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-foreground-600">
                    Chưa có dữ liệu giá vàng. Nhấn "Cập nhật giá ngay" để lấy dữ liệu từ API.
                  </td>
                </tr>
              ) : (
                prices.map((p) => (
                  <tr key={p.type_code} className="border-t border-background-200/40 hover:bg-background-100/60 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-foreground-950">{p.name}</p>
                      <p className="text-xs text-foreground-600">{p.type_code}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-foreground-900 whitespace-nowrap">{Number(p.buy).toLocaleString('vi-VN')}đ</td>
                    <td className="px-4 py-3 text-right font-semibold text-foreground-900 whitespace-nowrap">{Number(p.sell).toLocaleString('vi-VN')}đ</td>
                    <td className={`px-4 py-3 text-right text-sm font-semibold whitespace-nowrap ${Number(p.change_percent) >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {Number(p.change_percent) >= 0 ? '+' : ''}{Number(p.change_percent).toFixed(2)}%
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-foreground-600 whitespace-nowrap">
                      {new Date(p.updated_at).toLocaleString('vi-VN')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}